import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';

import { appendHeader } from '@confluence/network/entry-points/appendHeader';

import { HEADER_KEY, getEmbeddedConfluenceHeader } from './getEmbeddedConfluenceHeader';

// We only want to add custom headers to confluence apis since adding to external api might result CORS issue because of security.
const ALLOWED_API_PATHS = /\/wiki\/|\/cgraphql|\/gateway\/api\//;

function shouldAddEmbeddedConfluenceHeader(url) {
	// We add the shimUrl to the allowedAPIPath so that the custom headers are passed to the redirected call. Without the custom headers, the backend won't be able to authenticate for JSM customer accounts.
	const observableObject = getIframeObservable_DO_NOT_USE();
	const shimUrl = observableObject?.object?.navigationPolicy?.shimUrl;
	const allowedAPIPath = shimUrl
		? RegExp(`${ALLOWED_API_PATHS.source}|${shimUrl}`)
		: ALLOWED_API_PATHS;
	return url?.match(allowedAPIPath);
}

// Add embedded Confluence header to most requests to allow unlicensed access and embedded Confluence on first party custom domains.
// It will only add the header when all the required header fields are present.
// https://hello.atlassian.net/wiki/spaces/CSOL/pages/1078550026/FE+Integration+with+Northstar+MVP+for+Unlicensed+User+Access
export const httpInterceptor = () => {
	if (getEmbeddedConfluenceHeader()) {
		interceptFetch();
		interceptXMLHttpRequest();
	}
};

const interceptFetch = () => {
	/* eslint-disable no-restricted-properties */

	const { fetch: originalFetch } = window;

	if (!window.fetch['__INTERCEPTED__']) {
		// eslint-disable-next-line no-restricted-properties
		window.fetch = (resource, requestInit) => {
			const url = typeof resource === 'string' ? resource : (resource as Request).url;

			if (shouldAddEmbeddedConfluenceHeader(url)) {
				const header = getEmbeddedConfluenceHeader();
				if (header) {
					// When resource is a Request object, adding headers in requestInit will override headers that is set in the Request obj if there's one.
					// So we need to add headers at the correct place
					let headers = requestInit?.headers || resource['headers'];
					if (!headers) {
						requestInit = requestInit || {};
						requestInit.headers = requestInit.headers || {};
						headers = requestInit.headers;
					}
					appendHeader(headers, HEADER_KEY, header[HEADER_KEY]);
				}
			}

			return originalFetch(resource, requestInit);
		};
		window.fetch['__INTERCEPTED__'] = true;
	}
	/* eslint-enable no-restricted-properties */
};

const interceptXMLHttpRequest = () => {
	// We need to monkey patch XMLHttpRequest.open because we are not able to extract url from XMLHttpRequest object
	if (!XMLHttpRequest.prototype.open['__INTERCEPTED__']) {
		const originalOpen = XMLHttpRequest.prototype.open;
		XMLHttpRequest.prototype.open = function (...args) {
			this['__URL__'] = args[/* url */ 1];
			// @ts-ignore
			return originalOpen.apply(this, args);
		};
		XMLHttpRequest.prototype.open['__INTERCEPTED__'] = true;
	}

	if (!XMLHttpRequest.prototype.send['__INTERCEPTED__']) {
		const originalSend = XMLHttpRequest.prototype.send;

		XMLHttpRequest.prototype.send = function (body) {
			if (shouldAddEmbeddedConfluenceHeader(this['__URL__'])) {
				const header = getEmbeddedConfluenceHeader()?.[HEADER_KEY];

				header &&
					!this['isEmbeddedConfluenceHeaderSet'] &&
					this.setRequestHeader(HEADER_KEY, header);
			}

			originalSend.call(this, body);
		};
		XMLHttpRequest.prototype.send['__INTERCEPTED__'] = true;
	}

	// Avoid adding duplicate embedded Confluence header value
	if (!XMLHttpRequest.prototype.setRequestHeader['__INTERCEPTED__']) {
		const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

		XMLHttpRequest.prototype.setRequestHeader = function (key, value) {
			if (key.toLowerCase() === HEADER_KEY) {
				this['isEmbeddedConfluenceHeaderSet'] = true;
			}

			originalSetRequestHeader.call(this, key, value);
		};
		XMLHttpRequest.prototype.setRequestHeader['__INTERCEPTED__'] = true;
	}
};
